import React, { FC } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { Actions, Subjects } from '@jaam/web-core';
import ProtectedRoutes from './ProtectedRoutes';
import PublicRoutes from './PublicRoutes';
import NotFound from '../pages/notFound/notFound';

/** Dynamic import that default-exports a React component (used with React.lazy). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LazyModule = { default: React.ComponentType<any> };

const lazyRetry = (
  componentImport: () => Promise<LazyModule>,
  name: string,
): Promise<LazyModule> => {
  return new Promise((resolve, reject) => {
    const hasRefreshed = JSON.parse(
      window.sessionStorage.getItem(`retry-${name}-refreshed`) || 'false',
    );
    componentImport()
      .then((component: LazyModule) => {
        window.sessionStorage.setItem(`retry-${name}-refreshed`, 'false');
        resolve(component);
      })
      .catch((error: unknown) => {
        if (!hasRefreshed) {
          window.sessionStorage.setItem(`retry-${name}-refreshed`, 'true');
          window.location.reload();
          return;
        }
        reject(error);
      });
  });
};

/** Build route handle for ability checks. Reduces repetition. */
const handle = (action: Actions, subject: Subjects) => [
  { action, subject },
  { action: 'view' as Actions, subject: 'all' as Subjects },
];

const handleAll = () => [{ action: 'view' as Actions, subject: 'all' as Subjects }];

// ─── Lazy pages: global & public ─────────────────────────────────────────────
const Home = React.lazy(() =>
  lazyRetry(
    () => import(/* webpackChunkName: "Home" */ '../pages/home/home'),
    'Home',
  ),
);

const Login: FC = () => {
  window.location.href = `${process.env.NX_AUTH_PUBLIC_URL}/login?appReferer=payrollr`;
  return null;
};

// ─── Lazy pages: company ────────────────────────────────────────────────────
const Companies = React.lazy(() =>
  lazyRetry(
    () => import(/* webpackChunkName: "Companies" */ '../pages/companies/Companies'),
    'Companies',
  ),
);
const Help = React.lazy(() =>
  lazyRetry(
    () => import(/* webpackChunkName: "Help" */ '../pages/help/Help'),
    'Help',
  ),
);
const SelectPlanPage = React.lazy(() =>
  lazyRetry(
    () =>
      import(/* webpackChunkName: "SelectPlan" */ '../pages/selectPlan/selectPlan'),
    'SelectPlan',
  ),
);
const CompanyContactAdminPage = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        /* webpackChunkName: "CompanyContactAdmin" */ '../pages/company/contactAdmin/CompanyContactAdminPage'
      ),
    'CompanyContactAdminPage',
  ),
);
const InformationsCompany = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        /* webpackChunkName: "InformationsCompany" */ '../pages/company/info/InformationPage'
      ),
    'InformationsCompany',
  ),
);
const Members = React.lazy(() =>
  lazyRetry(
    () =>
      import(/* webpackChunkName: "Members" */ '../pages/company/members/Members'),
    'Members',
  ),
);
// ─── Lazy pages: staff & contracts ───────────────────────────────────────────
const Staff = React.lazy(() =>
  lazyRetry(
    () => import(/* webpackChunkName: "Staff" */ '../pages/staff/Staff'),
    'Staff',
  ),
);
const AllContracts = React.lazy(() =>
  lazyRetry(
    () =>
      import(/* webpackChunkName: "AllContracts" */ '../pages/contract/AllContracts'),
    'AllContracts',
  ),
);
const StaffRequestTab = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        /* webpackChunkName: "StaffRequest" */ '../pages/staffRequests/StaffRequestTab'
      ),
    'StaffRequestTab',
  ),
);

// ─── Lazy pages: pay (periods, slips, variables, invoices) ───────────────────
const PAY_PERIODS_DEFAULT = 'periods?period=ALL';

const PayPeriod = React.lazy(() =>
  lazyRetry(
    () =>
      import(/* webpackChunkName: "PayPeriod" */ '../pages/payPeriod/PayPeriod'),
    'PayPeriod',
  ),
);
const PaySlipPage = React.lazy(() =>
  lazyRetry(
    () => import(/* webpackChunkName: "PaySlipPage" */ '../pages/paySlips/paySlip'),
    'PaySlipPage',
  ),
);
const PayVariable = React.lazy(() =>
  lazyRetry(
    () =>
      import(/* webpackChunkName: "PayVariable" */ '../pages/payVariable/PayVariable'),
    'PayVariable',
  ),
);
const StaffInvoicesPeriod = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        /* webpackChunkName: "StaffInvoices" */ '../pages/payPeriod/staff-invoices/staffInvoices'
      ),
    'StaffInvoicesPeriod',
  ),
);
const TaxesPage = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        /* webpackChunkName: "TaxesPage" */ '../pages/payPeriod/components/taxesDetails'
      ),
    'TaxesPage',
  ),
);

// ─── Lazy pages: payroll elements, teams, credentials ────────────────────────
const PayrollElement = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        /* webpackChunkName: "PayrollElement" */ '../pages/payrollElement/PayrollElement'
      ),
    'PayrollElement',
  ),
);
const Teams = React.lazy(() =>
  lazyRetry(
    () => import(/* webpackChunkName: "Teams" */ '../pages/teams/Team'),
    'Teams',
  ),
);
const CredentialsPage = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        /* webpackChunkName: "CredentialsPage" */ '../pages/credentials/Credentials'
      ),
    'CredentialsPage',
  ),
);

// ─── Lazy pages: staff invoices, transactions, admin, settings ──────────────
const StaffInvoice = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        /* webpackChunkName: "StaffInvoice" */ '../pages/staffInvoice/StaffInvoice'
      ),
    'StaffInvoice',
  ),
);
const CreateStaffInvoice = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        /* webpackChunkName: "CreateStaffInvoice" */ '../pages/staffInvoice/CreateStaffInvoice'
      ),
    'CreateStaffInvoice',
  ),
);
const Transactions = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        /* webpackChunkName: "Transactions" */ '../pages/transaction/TransactionsPage'
      ),
    'Transactions',
  ),
);
const Migration = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        /* webpackChunkName: "Migration" */ '../pages/leaveMigration/Migration'
      ),
    'Migration',
  ),
);
const Admin = React.lazy(() =>
  lazyRetry(
    () => import(/* webpackChunkName: "Admin" */ '../pages/admin/Admin'),
    'Admin',
  ),
);
const AdminList = React.lazy(() =>
  lazyRetry(
    () => import(/* webpackChunkName: "AdminList" */ '../pages/admin/AdminList'),
    'AdminList',
  ),
);
const Settings = React.lazy(() =>
  lazyRetry(
    () => import(/* webpackChunkName: "Settings" */ '../pages/settings/Settings'),
    'Settings',
  ),
);
const InvitePayVariableContainer = React.lazy(() =>
  lazyRetry(
    () =>
      import(
        /* webpackChunkName: "InvitePayVariableContainer" */ '../pages/payVariable/InvitePayVariableContainer'
      ),
    'InvitePayVariableContainer',
  ),
);

// ─── Route config ───────────────────────────────────────────────────────────
// Explicit type avoids reference to @remix-run/router in emitted .d.ts (portability).
type RouterInstance = ReturnType<typeof createBrowserRouter>;

export const MainRoutes: RouterInstance = createBrowserRouter(
  [
    {
      path: '/',
      element: <ProtectedRoutes />,
      children: [
        {
          path: '*',
          element: <NotFound />,
          handle: handleAll(),
        },
        {
          path: '/',
          children: [
            { path: '/', element: <Navigate replace to="companies" />, handle: handleAll() },
            { path: 'home', element: <Home />, handle: handle('view', 'Home') },
            { path: 'companies', element: <Companies />, handle: handle('view', 'Company') },
            { path: 'help', element: <Help />, handle: handleAll() },
            { path: 'company/plan', element: <SelectPlanPage />, handle: handleAll() },
            {
              path: 'company/contact-admin',
              element: <CompanyContactAdminPage />,
              handle: handleAll(),
            },
            { path: 'transactions', element: <Transactions />, handle: handle('view', 'Company') },

            // Staff
            {
              path: 'staff',
              children: [
                {
                  index: true,
                  element: <Staff />,
                  handle: handle('view', 'Staff'),
                },
                {
                  path: ':selectedItemId/contracts',
                  element: <AllContracts />,
                  handle: handle('view', 'Contract'),
                },
                {
                  path: ':staffId/contracts/:newContract',
                  element: <AllContracts />,
                  handle: handle('create', 'Contract'),
                },
              ],
            },

            // Company (info, members, payroll elements, teams, credentials)
            {
              path: 'company',
              children: [
                {
                  path: 'information',
                  element: <InformationsCompany />,
                  handle: handle('view', 'Company'),
                },
                {
                  path: 'members',
                  element: <Members />,
                  handle: handle('update', 'Company'),
                },
                {
                  path: 'payroll-elements',
                  element: <PayrollElement />,
                  handle: handle('view', 'PayrollElement'),
                },
                {
                  path: 'teams',
                  element: <Teams />,
                  handle: handle('view', 'Teams' as Subjects),
                },
                {
                  path: 'credentials',
                  element: <CredentialsPage />,
                  handle: handle('view', 'Credential'),
                },
              ],
            },

            // Pay (periods, payslips, pay-variables, staff-invoices, taxes)
            {
              path: 'pay',
              handle: handle('view', 'PayPeriod'),
              children: [
                {
                  index: true,
                  element: <Navigate replace to={PAY_PERIODS_DEFAULT} />,
                },
                {
                  path: 'periods',
                  element: <PayPeriod />,
                  handle: handle('view', 'PayPeriod'),
                },
                {
                  path: 'periods/:periodId/payslips',
                  element: <PaySlipPage />,
                  handle: handle('view', 'PayPeriod'),
                },
                {
                  path: 'periods/:periodId/sessions/:payrollSessionId/pay-variables',
                  element: <PayVariable />,
                  handle: handle('view', 'PayVariable' as Subjects),
                },
                {
                  path: 'periods/:periodId/staff-invoices',
                  element: <StaffInvoicesPeriod />,
                  handle: handle('view', 'PayPeriod'),
                },
                {
                  path: 'periods/:periodId/taxes',
                  element: <TaxesPage />,
                  handle: handle('view', 'PayPeriod'),
                },
              ],
            },

            {
              path: 'staffRequest',
              element: <StaffRequestTab />,
              handle: handle('view', 'StaffRequest' as Subjects),
            },
            {
              path: 'staff-invoices',
              element: <StaffInvoice />,
              handle: handle('view', 'StaffInvoice'),
            },
            {
              path: 'staff-invoices/create',
              element: <CreateStaffInvoice />,
            },
            {
              path: 'staff-invoices/:selectedItemId/create',
              element: <CreateStaffInvoice />,
            },
            {
              path: 'migration',
              element: <Migration />,
              handle: handleAll(),
            },
            {
              path: 'admin',
              element: <Admin />,
              handle: handle('view', 'Admin'),
            },
            {
              path: 'admin/list',
              element: <AdminList />,
              handle: handle('view', 'Admin'),
            },
            {
              path: 'settings',
              element: <Settings />,
              handle: handle('view', 'Company'),
            },
          ],
        },
      ],
    },
    {
      path: '/',
      element: <PublicRoutes />,
      children: [
        { path: 'login', element: <Login /> },
        {
          path: 'secure-access/:inviteToken',
          children: [
            { index: true, element: <Navigate to="request-otp" replace /> },
            { path: ':step', element: <InvitePayVariableContainer /> },
          ],
        },
      ],
    },
  ],
  window,
);
